import { defineStore } from 'pinia'
import { ref } from 'vue'

export type TopBar = {
  title: string | null
  show: boolean
  showBackButton: boolean
  showToggle: boolean
}

export enum ScanInputType {
  CAMERA = 'CAMERA',
  EXTERNAL_SENSOR = 'EXTERNAL_SENSOR'
}

export enum ManufacturingOrderStatus {
  PLANNED = 'PLANNED',
  PRODUCTION = 'PRODUCTION',
  COMPLETED = 'COMPLETED',
  CANCELLED = 'CANCELLED',
  PAUSED = 'PAUSED'
}

export type ManufacturingOrder = {
  id: string
  productId: string
  orderNumber: number
  status: ManufacturingOrderStatus
  productUnitCode: string
  primaryColorName?: string
  secondaryColorName?: string
  primaryColorHex?: string
  secondaryColorHex: string
  year: number
  total: number
  checked: number
  problem: number
  imageUrl?: string
  dataLoadedAt: Date
  material: string
  description: string
  size: string
}

export type MoProduct = {
  id: string
  description: string
  size: string
  year: string
  primaryColorHex: string
  secondaryColorHex: string
  serialNumber: string
  loadedAt?: Date
  autoGeneratedSerialNumber?: boolean
}

export type ReadProduct = {
  description: string
  size: string
  year: string
  primaryColorHex: string
  secondaryColorHex: string
  serialNumber: string
  loadedAt?: Date
}

export const useAppStore = defineStore(
  'app',
  () => {
    const topBar = ref<TopBar>({
      title: '',
      show: true,
      showBackButton: false,
      showToggle: false
    })
    const scanInputType = ref<ScanInputType>(ScanInputType.CAMERA)
    const currentManufacturingOrder = ref<ManufacturingOrder | null>(null)
    const currentMoProduct = ref<MoProduct | null>(null)
    const currentTag = ref<string | null>(null)
    const currentSerialNumber = ref<string | null>(null)
    const currentReadProduct = ref<ReadProduct | null>(null)
    const currentTagReadProduct = ref<string | null>(null)
    const currentSerialNumberReadProduct = ref<string | null>(null)
    const nonConformityView = ref<boolean>(false)
    const appCurrentPage = ref<string>('MO')

    const actions = {
      topBar: {
        setTopBarTitle(title: string | null) {
          topBar.value.title = title
        },
        setTopBarVisibility(visible: boolean) {
          topBar.value.show = visible
        }
      },

      setScanInputType(type: ScanInputType) {
        scanInputType.value = type
      },

      setManufacturingOrder(order: ManufacturingOrder | null) {
        currentManufacturingOrder.value = order
      },

      setCurrentMoProduct(product: MoProduct | null) {
        currentMoProduct.value = product
      },

      setCurrentReadProduct(product: ReadProduct | null) {
        currentReadProduct.value = product
      },

      setCurrentTag(tag: string | null) {
        currentTag.value = tag
      },

      setCurrentTagReadProduct(tag: string | null) {
        currentTagReadProduct.value = tag
      },

      clearCurrentTag() {
        currentTag.value = null
        currentMoProduct.value = null
      },

      setCurrentSerialNumber(serialNumber: string | null) {
        currentSerialNumber.value = serialNumber
      },

      setCurrentSerialNumberReadProduct(serialNumber: string | null) {
        currentSerialNumberReadProduct.value = serialNumber
      },

      clearCurrentSerialNumber() {
        currentSerialNumber.value = null
      },

      setNonConformityView(on: boolean) {
        nonConformityView.value = on
      },

      setCurrentPage(page: string) {
        appCurrentPage.value = page
      }
    }

    return {
      topBar,
      scanInputType,
      currentTag,
      currentSerialNumber,
      nonConformityView,
      currentManufacturingOrder,
      currentMoProduct,
      currentReadProduct,
      currentTagReadProduct,
      currentSerialNumberReadProduct,
      appCurrentPage,
      actions
    }
  },
  {
    persist: true
  }
)
